/** @jsxImportSource theme-ui */
import { Box, Label, Flex, Grid, Input } from 'theme-ui';
import { useState } from 'react';
import { Formik, FormikProps } from 'formik';
import qs from 'qs';
import { nanoid } from 'nanoid';
import { useSearchParams } from 'react-router-dom';
import { endOfMonth, endOfQuarter, format, startOfMonth, startOfQuarter, subDays, subMonths } from 'date-fns';
import Button from '@/components/Button';
import { GrayFormikField } from '@/components/GrayFormikField';
import CustomDatePicker from '@/components/CustomDatePicker';
import { useGetCompaniesQuery } from '@/store/apiService';

export type UsageAuditFilterValues = {
    From: string;
    To: string;
    Granularity: string;
    CompanyId: string;
    UsageType: string;
    OnlyDifferences: boolean;
    IncludeRecount: boolean;
    MinorThresholdPercent: string;
};

const DAY = 'yyyy-MM-dd';

// The API audits UTC days, so "yesterday" is the UTC calendar day expressed as a local Date for the pickers.
const utcYesterday = () => {
    const now = new Date();
    return new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1);
};

const clampToYesterday = (date: Date) => (date > utcYesterday() ? utcYesterday() : date);

type Preset = {
    label: string;
    tenantWide: boolean;
    hidden?: () => boolean;
    range: () => { From: string; To: string; Granularity: string };
};

const presets: Preset[] = [
    {
        label: 'Yesterday',
        tenantWide: true,
        range: () => ({ From: format(utcYesterday(), DAY), To: format(utcYesterday(), DAY), Granularity: 'Day' }),
    },
    {
        label: 'Last 7 days',
        tenantWide: true,
        range: () => ({ From: format(subDays(utcYesterday(), 6), DAY), To: format(utcYesterday(), DAY), Granularity: 'Day' }),
    },
    {
        label: 'This month',
        tenantWide: true,
        range: () => ({ From: format(startOfMonth(utcYesterday()), DAY), To: format(utcYesterday(), DAY), Granularity: 'Week' }),
    },
    {
        label: 'Last month',
        tenantWide: true,
        range: () => {
            const month = subMonths(utcYesterday(), 1);
            return { From: format(startOfMonth(month), DAY), To: format(endOfMonth(month), DAY), Granularity: 'Week' };
        },
    },
    ...[1, 2, 3, 4].map((quarter): Preset => {
        const start = () => startOfQuarter(new Date(utcYesterday().getFullYear(), (quarter - 1) * 3, 1));
        return {
            label: `Q${quarter}`,
            tenantWide: false,
            hidden: () => start() > utcYesterday(),
            range: () => ({
                From: format(start(), DAY),
                To: format(clampToYesterday(endOfQuarter(start())), DAY),
                Granularity: 'Month',
            }),
        };
    }),
];

export const defaultFilterValues = (): UsageAuditFilterValues => ({
    From: format(utcYesterday(), DAY),
    To: format(utcYesterday(), DAY),
    Granularity: 'Day',
    CompanyId: '',
    UsageType: '',
    OnlyDifferences: false,
    IncludeRecount: true,
    MinorThresholdPercent: '',
});

const valuesFromSearch = (search: string): UsageAuditFilterValues => {
    const defaults = defaultFilterValues();
    const parsed = qs.parse(search) as Record<string, string | undefined>;
    const text = (key: keyof UsageAuditFilterValues) =>
        typeof parsed[key] === 'string' ? (parsed[key] as string) : (defaults[key] as string);
    return {
        From: text('From'),
        To: text('To'),
        Granularity: text('Granularity'),
        CompanyId: text('CompanyId'),
        UsageType: text('UsageType'),
        MinorThresholdPercent: text('MinorThresholdPercent'),
        OnlyDifferences: parsed.OnlyDifferences === undefined ? defaults.OnlyDifferences : parsed.OnlyDifferences === 'true',
        IncludeRecount: parsed.IncludeRecount === undefined ? defaults.IncludeRecount : parsed.IncludeRecount !== 'false',
    };
};

const UsageAuditFilters: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [companySearch, setCompanySearch] = useState('');
    const companies = useGetCompaniesQuery({ Take: 20, Search: companySearch });

    const toDate = (value: string) => (value ? new Date(`${value}T00:00:00`) : null);

    return (
        <Formik
            initialValues={valuesFromSearch(searchParams.toString())}
            enableReinitialize
            onSubmit={(values, { setSubmitting }) => {
                setSearchParams(qs.stringify({ ...values, rand: nanoid(6) }));
                setSubmitting(false);
            }}
        >
            {(props: FormikProps<UsageAuditFilterValues>) => (
                <>
                    <Flex sx={{ gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        {presets
                            .filter((preset) => !preset.hidden?.())
                            .map((preset) => (
                                <Button
                                    key={preset.label}
                                    variant="secondary"
                                    type="button"
                                    disabled={!preset.tenantWide && !props.values.CompanyId}
                                    title={
                                        !preset.tenantWide && !props.values.CompanyId
                                            ? 'Quarter ranges need a company (tenant-wide audits are capped at 31 days)'
                                            : undefined
                                    }
                                    onClick={() => {
                                        const range = preset.range();
                                        props.setFieldValue('From', range.From);
                                        props.setFieldValue('To', range.To);
                                        props.setFieldValue('Granularity', range.Granularity);
                                    }}
                                >
                                    {preset.label}
                                </Button>
                            ))}
                    </Flex>
                    <Grid
                        sx={{
                            '@media (width > 700px)': {
                                gridTemplateColumns: 'repeat(3, minmax(180px, 1fr))',
                            },
                        }}
                    >
                        <Box>
                            <Label>From (UTC day)</Label>
                            <CustomDatePicker
                                variation="normal"
                                dateFormat={DAY}
                                maxDate={utcYesterday()}
                                field={{ name: 'From', value: toDate(props.values.From) }}
                                onChange={(value: Date) => props.setFieldValue('From', format(value, DAY))}
                            />
                        </Box>
                        <Box>
                            <Label>To (UTC day)</Label>
                            <CustomDatePicker
                                variation="normal"
                                dateFormat={DAY}
                                maxDate={utcYesterday()}
                                field={{ name: 'To', value: toDate(props.values.To) }}
                                onChange={(value: Date) => props.setFieldValue('To', format(value, DAY))}
                            />
                        </Box>
                        <GrayFormikField
                            name="Granularity"
                            type="select"
                            label="Bucket"
                            options={[
                                { value: 'Day', label: 'Day' },
                                { value: 'Week', label: 'Week (ISO)' },
                                { value: 'Month', label: 'Month' },
                                { value: 'Quarter', label: 'Quarter' },
                            ]}
                        />
                        <Box variant="form-field">
                            <GrayFormikField
                                name="CompanyId"
                                value={props.values.CompanyId}
                                type="dropdownsearch"
                                label="Company (optional)"
                                placeholder="All non-demo companies (max 31 days)"
                                onSearchChange={(val: string) => setCompanySearch(val)}
                                onSelect={(item: any) => props.setFieldValue('CompanyId', item?.value ?? '')}
                                items={companies.data?.Results?.map((company: any) => ({
                                    value: company.Id,
                                    label: `${company.Name} (${company.Email})`,
                                }))}
                                isLoading={companies.isFetching}
                                total={companies.data?.Total}
                            />
                        </Box>
                        <GrayFormikField
                            name="UsageType"
                            type="select"
                            label="Usage type"
                            options={[
                                { value: '', label: 'Bookings and SMS' },
                                { value: 'BookingUsage', label: 'Bookings (BM.BOOK.FEE)' },
                                { value: 'SmsUsage', label: 'SMS (BM.SMS / BM.SMS2)' },
                            ]}
                        />
                        <Box>
                            <Label>Minor threshold (%)</Label>
                            <Input
                                name="MinorThresholdPercent"
                                type="number"
                                min={0}
                                step={0.5}
                                placeholder="server default"
                                backgroundColor="gray.1"
                                value={props.values.MinorThresholdPercent}
                                onChange={(e) => props.setFieldValue('MinorThresholdPercent', e.target.value)}
                            />
                        </Box>
                        <Box sx={{ alignSelf: 'end' }}>
                            <GrayFormikField type="checkbox" label="Only differences" name="OnlyDifferences" />
                        </Box>
                        <Box sx={{ alignSelf: 'end' }}>
                            <GrayFormikField type="checkbox" label="Recount bookings/SMS" name="IncludeRecount" />
                        </Box>
                    </Grid>
                    <Flex p="2" sx={{ width: '100%', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="secondary"
                            type="button"
                            onClick={() => {
                                props.resetForm({ values: defaultFilterValues() });
                                setSearchParams('');
                            }}
                        >
                            Clear
                        </Button>
                        <Button
                            isLoading={props.isSubmitting}
                            disabled={props.isSubmitting}
                            type="submit"
                            onClick={props.submitForm}
                        >
                            Run audit
                        </Button>
                    </Flex>
                </>
            )}
        </Formik>
    );
};

export default UsageAuditFilters;
